import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { createOrderV2Client } from '../api/client';
import { orderV2QueryKeys } from '../api/queryKeys';

type RateQuote = { pay: number; cost: number };

// Auto-fetch rate quote (pay/cost in cents) whenever the billing
// account, skill, and address are all set. The quote prefills
// pay_rate/cost_rate on the job draft so the ScheduleStep can show the
// rate card for STATIC (non-markup) billing accounts. We only prefill
// when the job has not been edited yet — that guard lives in the
// orchestrator's `applyRateQuoteReducer` (unit-tested in
// `../stateReducers.ts`), so a tenant admin's manual edits are not
// clobbered by background refetches even if the callback fires after
// the user has typed.
const useRateQuote = (
  billingAccountId: number | null,
  skillId: number | null,
  addressId: number | null,
  alreadyHasRates: boolean,
  onQuote: (pay: number, cost: number) => void,
) => {
  const enabled =
    billingAccountId != null &&
    skillId != null &&
    addressId != null &&
    !alreadyHasRates;

  const query = useQuery<RateQuote | null>({
    queryKey:
      billingAccountId != null && skillId != null && addressId != null
        ? orderV2QueryKeys.rateQuote(billingAccountId, skillId, addressId)
        : ['orders_v2', 'rate_quote', 'disabled'],
    queryFn: () =>
      createOrderV2Client
        .getRateQuote(
          billingAccountId as number,
          skillId as number,
          addressId as number,
        )
        .then((quote) => quote ?? null),
    enabled,
  });

  useEffect(() => {
    const data = query.data;
    if (!data) return;
    if (alreadyHasRates) return;
    onQuote(data.pay, data.cost);
  }, [query.data, alreadyHasRates, onQuote]);
};

export default useRateQuote;
